/* =============================================================================
 * M.M.LaFleur — hero auto-advance with a countdown ring.
 *
 * Reads data-mm-hero-interval off [data-mm-hero] (0 / 5000 / 10000 / 15000,
 * set from the section's "Auto-advance slides" setting) and steps the hero when
 * the interval elapses, filling a progress ring around the next arrow as it
 * counts down.
 *
 * WHY A rAF CLOCK RATHER THAN A CSS ANIMATION
 * -------------------------------------------
 * The obvious build is a keyframe animation on the ring with
 * animation-duration set from Liquid, advancing the hero on animationend — one
 * clock driving both the visual and the step, so they cannot drift.
 *
 * It would break badly here. mm-core.css carries:
 *
 *   @media (prefers-reduced-motion: reduce) {
 *     .mm-scope *, ... { animation-duration: 0.001ms !important; }
 *   }
 *
 * The hero is inside .mm-scope, so for a visitor who has asked for reduced
 * motion that animation would complete in a thousandth of a millisecond and
 * animationend would fire immediately — over and over, spinning the carousel as
 * fast as the browser could paint. A requestAnimationFrame clock is not
 * reachable by that override, and it makes pause/resume exact rather than
 * approximate.
 *
 * Under reduced motion the hero starts PAUSED rather than having auto-advance
 * removed outright: the pause control shows Play, and a visitor who presses it
 * has asked for the motion explicitly, so it runs. That is the correct
 * behaviour for moving content and not merely a workaround.
 *
 * PAUSING
 * -------
 * Auto-advancing content has to be stoppable (WCAG 2.2.2). It pauses while the
 * pointer is over the hero, while keyboard focus is inside it, and while the
 * tab is in the background — the last one also stops a backgrounded tab from
 * racing through slides on rAF catch-up.
 *
 * Hover and focus are transient and invisible to a touch visitor, so there is
 * also an explicit Pause/Play button ([data-mm-hero-pause], rendered by
 * mm_hero.liquid). Its state is held separately in userPaused: hovering off
 * the hero must never undo a pause the visitor asked for.
 *
 * The same button stops the hero's looping slide videos, and marks each one
 * data-mm-user-paused="true" — the flag mm-editorial.js and
 * mm-video-controls.js both honour, so neither restarts a video the visitor
 * stopped. It works on a video-only hero with auto-advance off, which is why
 * init() binds the button before deciding whether there is a clock to run.
 *
 * STEPPING REUSES THE SECTION'S OWN HANDLER
 * -----------------------------------------
 * This does not implement stepping. It calls .click() on the next arrow, so the
 * capture-phase handler already in mm_hero.liquid does the work — including
 * clearing the copy's animation-delay, which is the thing that would otherwise
 * hold the incoming slide's text at opacity 0.
 *
 * That handler calls stopPropagation(), not stopImmediatePropagation(), and
 * both listeners sit on document: propagation is stopped between NODES, never
 * between listeners on the same node, so the reset below still runs on a manual
 * click. It is registered later than the section's (that one is inline and runs
 * at parse time; this file is deferred), which is exactly the order wanted —
 * the hero steps first, then the clock resets.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmHeroAutoplayBound) return;
  window.__mmHeroAutoplayBound = true;

  /* Must match r on the <circle> in mm_hero.liquid: 2 * PI * 19.5. */
  var CIRCUMFERENCE = 2 * Math.PI * 19.5;

  var hero = null;
  var ring = null;
  var duration = 0;
  var elapsed = 0;
  var last = 0;
  var frame = null;
  var paused = false;
  var userPaused = false;
  var button = null;

  function reducedMotion() {
    return !!(window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  function paint(progress) {
    if (!ring) return;
    ring.style.strokeDasharray = CIRCUMFERENCE.toFixed(3);
    ring.style.strokeDashoffset = (CIRCUMFERENCE * (1 - progress)).toFixed(3);
  }

  function reset() {
    elapsed = 0;
    last = 0;
    paint(0);
  }

  function stop() {
    if (frame) window.cancelAnimationFrame(frame);
    frame = null;
    hero = null;
    ring = null;
    button = null;
    duration = 0;
  }

  function syncButton() {
    if (!button) return;
    button.setAttribute("data-mm-paused", String(userPaused));
    var label = button.getAttribute(userPaused ? "data-label-play" : "data-label-pause");
    if (label) button.setAttribute("aria-label", label);
  }

  function videos() {
    return hero ? [].slice.call(hero.querySelectorAll("video")) : [];
  }

  /* Only the active slide's video is started: the others are preload="none"
     and invisible, so playing them would spend bandwidth on nothing. */
  function playActiveVideos() {
    videos().forEach(function (video) {
      var slide = video.closest("[data-mm-hero-slide]");
      if (slide && slide.getAttribute("data-active") !== "true") return;
      video.muted = true;
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  function setUserPaused(value) {
    userPaused = value;
    syncButton();
    videos().forEach(function (video) {
      if (value) {
        video.setAttribute("data-mm-user-paused", "true");
        video.pause();
      } else {
        video.removeAttribute("data-mm-user-paused");
        /* Lets mm-video-controls.js's reduced-motion guard stand aside: the
           visitor pressed Play, so this motion was asked for. */
        video.setAttribute("data-mm-user-played", "true");
      }
    });
    if (!value) {
      last = 0;
      playActiveVideos();
    }
  }

  function tick(now) {
    frame = window.requestAnimationFrame(tick);

    /* Swallow the time spent paused rather than accumulating it, so releasing
       a hover resumes from where the ring actually is. */
    if (paused || userPaused || document.hidden) {
      last = now;
      return;
    }
    if (!last) last = now;

    elapsed += now - last;
    last = now;

    var progress = elapsed / duration;
    if (progress > 1) progress = 1;
    paint(progress);

    if (progress < 1) return;

    reset();
    var next = hero.querySelector('[data-mm-hero-nav="1"]');
    if (next) next.click();
  }

  function init() {
    stop();

    var el = document.querySelector("[data-mm-hero]");
    if (!el) return;

    hero = el;
    button = hero.querySelector("[data-mm-hero-pause]");
    paused = false;
    userPaused = reducedMotion();
    syncButton();
    if (userPaused) {
      videos().forEach(function (video) { video.pause(); });
    }

    var ms = parseInt(el.getAttribute("data-mm-hero-interval"), 10);
    /* Nothing to advance to — the button (if any) still governs video. */
    if (!ms || ms < 1 || el.querySelectorAll("[data-mm-hero-slide]").length < 2) return;

    duration = ms;
    ring = hero.querySelector("[data-mm-hero-ring]");

    reset();
    frame = window.requestAnimationFrame(tick);
  }

  /* Pointer and focus are read off document rather than bound to the hero, so
     a hero replaced by the theme editor or a Barba swap needs no rebinding. */
  document.addEventListener("pointerover", function (event) {
    if (!hero) return;
    paused = hero.contains(event.target);
  });

  document.addEventListener("focusin", function (event) {
    if (!hero) return;
    paused = hero.contains(event.target);
  });

  /* A pointer leaving the window entirely never fires pointerover elsewhere,
     which would otherwise leave the hero paused for good. */
  document.addEventListener("pointerleave", function () {
    paused = false;
  });

  document.addEventListener("visibilitychange", function () {
    if (!document.hidden) last = 0;
  });

  /* Any manual step restarts the countdown — advancing by hand and then being
     advanced again a moment later reads as a glitch. The step itself has
     already run (see the header note), so the newly active slide's video can
     be started here — unless the visitor has paused the hero. */
  document.addEventListener("click", function (event) {
    if (!hero || !event.target || !event.target.closest) return;
    var arrow = event.target.closest("[data-mm-hero-nav]");
    if (!arrow || !hero.contains(arrow)) return;
    reset();
    if (!userPaused) playActiveVideos();
  }, true);

  document.addEventListener("click", function (event) {
    if (!hero || !event.target || !event.target.closest) return;
    var toggle = event.target.closest("[data-mm-hero-pause]");
    if (!toggle || !hero.contains(toggle)) return;
    event.preventDefault();
    setUserPaused(!userPaused);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("shopify:section:unload", stop);
}());
